import {
  Component,
  OnInit,
  ChangeDetectorRef,
  NgZone,
  ChangeDetectionStrategy,
} from '@angular/core';

import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { environment } from '../../../environment/environment';
import { timeout, catchError, take } from 'rxjs/operators';
import { of } from 'rxjs';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Component({
  selector: 'app-duna-ics-online',
  standalone: true,
  imports: [
    MatButtonModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
  ],
  templateUrl: './duna-ics-online.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-ics-online.component.scss'],
})
export class DunaIcsOnlineComponent implements OnInit {
  userUuid: string | null = null;
  /** Direct backend ICS feed URL (for calendar subscriptions) */
  calendarUrl = '';
  loading = true;
  error: string | null = null;
  /** Fetched .ics content from API (API URL never exposed) */
  icsContent: string | null = null;
  private loadingGuardTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(take(1)).subscribe((params) => {
      this.userUuid = params['userUuid'] ?? this.getUserUuidFromUrl() ?? null;
      this.updateCalendarUrl();
      if (this.userUuid) {
        this.loadIcs();
      } else {
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  /** When route has no userUuid param (e.g. under catch-all), read UUID from browser URL path */
  private getUserUuidFromUrl(): string | null {
    const path =
      typeof window !== 'undefined'
        ? window.location.pathname
        : this.router.url;
    const segments = path.replace(/^\//, '').split('/');
    const i = segments.indexOf('ics');
    if (i !== -1 && segments[i + 1] === 'online' && segments[i + 2]) {
      const candidate = segments[i + 2];
      return UUID_REGEX.test(candidate) ? candidate : null;
    }
    return null;
  }

  private getApiBaseUrl(): string {
    let base = (environment.apiUrl || '').replace(/\/$/, '');
    if (base && !/^https?:\/\//i.test(base)) {
      base = 'https://' + base;
    }
    return base;
  }

  private updateCalendarUrl(): void {
    if (!this.userUuid) {
      this.calendarUrl = '';
      return;
    }
    const base = this.getApiBaseUrl();
    this.calendarUrl = base ? base + '/ics/online/' + this.userUuid : '';
  }

  private clearLoadingGuard(): void {
    if (this.loadingGuardTimer != null) {
      clearTimeout(this.loadingGuardTimer);
      this.loadingGuardTimer = null;
    }
  }

  /** Fetch .ics from API (URL stays in code, never shown to user). Uses same base as other API calls. */
  private loadIcs(): void {
    if (!this.userUuid) return;
    this.clearLoadingGuard();
    this.loading = true;
    this.error = null;
    this.icsContent = null;
    this.loadingGuardTimer = setTimeout(() => {
      this.loadingGuardTimer = null;
      if (this.loading) {
        this.ngZone.run(() => {
          this.loading = false;
          this.error =
            'Forespørgslen tog for lang tid. Brug linket direkte i din browser.';
          this.cdr.markForCheck();
        });
      }
    }, 20000);
    const url =
      this.calendarUrl || `${this.getApiBaseUrl()}/ics/online/${this.userUuid}`;
    this.http
      .get(url, { responseType: 'text' })
      .pipe(
        timeout(15000),
        catchError((err) => of({ _error: err }))
      )
      .subscribe({
        next: (body: unknown) => {
          this.clearLoadingGuard();
          this.ngZone.run(() => {
            const err = (body as { _error?: any })?._error;
            if (err) {
              this.loading = false;
              this.error =
                err.name === 'TimeoutError'
                  ? 'Forespørgslen tog for lang tid. Prøv igen senere.'
                  : err.status === 404
                  ? 'Kalenderen blev ikke fundet.'
                  : err.status === 0 ||
                    (err.message &&
                      /CORS|Network|Failed to fetch/i.test(err.message))
                  ? 'Kalenderen kunne ikke hentes (netværk/CORS). Brug linket direkte i din browser.'
                  : 'Kunne ikke hente kalender. Prøv igen senere.';
            } else {
              this.icsContent = body as string;
              this.loading = false;
            }
            this.cdr.markForCheck();
          });
        },
        error: (err: any) => {
          this.clearLoadingGuard();
          this.ngZone.run(() => {
            this.loading = false;
            this.error =
              err?.status === 404
                ? 'Kalenderen blev ikke fundet.'
                : err?.status === 0 ||
                  (err?.message &&
                    /CORS|Network|Failed to fetch/i.test(err.message))
                ? 'Kalenderen kunne ikke hentes (netværk/CORS). Brug linket direkte i din browser.'
                : err?.error?.message ||
                  'Kunne ikke hente kalender. Prøv igen senere.';
            this.cdr.markForCheck();
          });
        },
      });
  }

  downloadIcs(): void {
    if (!this.icsContent) return;
    const blob = new Blob([this.icsContent], {
      type: 'text/calendar; charset=utf-8',
    });
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = u;
    a.download = 'kalender.ics';
    a.click();
    URL.revokeObjectURL(u);
    this.snackBar.open('Download startet', undefined, { duration: 2000 });
  }

  copyPageLink(): void {
    if (!this.calendarUrl) return;
    navigator.clipboard.writeText(this.calendarUrl).then(
      () => this.snackBar.open('Link kopieret', undefined, { duration: 2000 }),
      () =>
        this.snackBar.open('Kunne ikke kopiere link', undefined, {
          duration: 3000,
        })
    );
  }
}
